import { indexOf, isNull } from 'lodash/fp';

import { ArkOpinion, LinkedOpinion, ArkMapOpinion, OpinionsTableView, OpinionsView, CountryOpinions, BritishOpinions } from './types';
import { ApplicationState } from '../../../../store/types';
import { AnalysisView } from '../../../documents/store/types';
import { ColumnSort, TableFilters } from '../../../shared/table/types';
import { getAllOpinionCustomFilters } from '../../../auth/login/store/selectors';
import { getMyOpinionsRoot } from '../../store/selectors';

export const getOpinionsView = (state: ApplicationState): OpinionsView => getMyOpinionsRoot(state).opinionsView;

export const getOpinionsTableView = (state: ApplicationState): OpinionsTableView => getMyOpinionsRoot(state).opinionsTableView;

export const getModalOpen = (state: ApplicationState): boolean => getMyOpinionsRoot(state).modalOpen;

export const getOpinionDetailsModalOpen = (state: ApplicationState): boolean => getMyOpinionsRoot(state).opinionDetailsModalOpen;

export const getFilesToUpload = (state: ApplicationState): File[] | null => getMyOpinionsRoot(state).filesToUpload;

export const getIsUploading = (state: ApplicationState): boolean => getMyOpinionsRoot(state).isUploading;

export const getIsOpening = (state: ApplicationState): boolean => getMyOpinionsRoot(state).isOpening;

export const getIsOpeningSummary = (state: ApplicationState): boolean => getMyOpinionsRoot(state).isOpeningSummary;

export const getIsLoading = (state: ApplicationState): boolean => getMyOpinionsRoot(state).isLoading;

export const getIsUpdatingOpinion = (state: ApplicationState): boolean => getMyOpinionsRoot(state).isUpdatingOpinionDetails;

export const getIncompleteOpinionUrl = (state: ApplicationState): string | null => getMyOpinionsRoot(state).incompleteOpinionUrl;

export const getIncompleteOpinion = (state: ApplicationState): ArkOpinion | null => getMyOpinionsRoot(state).incompleteOpinion;

export const getSelectedIncompleteOpinions = (state: ApplicationState): ArkOpinion[] => getMyOpinionsRoot(state).selectedIncompleteOpinions;

export const getOpinionDetailsUpdated = (state: ApplicationState): boolean => getMyOpinionsRoot(state).incompleteOpinionDetailsUpdated;

export const getIncompleteOpinions = (state: ApplicationState): ArkOpinion[] => getMyOpinionsRoot(state).incompleteOpinions;

export const getOpinions = (state: ApplicationState): ArkOpinion[] => getMyOpinionsRoot(state).opinions;

export const getOpinion = (state: ApplicationState): ArkOpinion | null => getMyOpinionsRoot(state).opinion;
export const getOpinionId = (state: ApplicationState): number | null => getOpinion(state)?.opinionId || null;

export const getOpinionUrl = (state: ApplicationState): string | null => getMyOpinionsRoot(state).opinionUrl;

export const getOpinionSummaryUrl = (state: ApplicationState): string | null => getMyOpinionsRoot(state).opinionSummaryUrl;

export const getAdditionalOpinions = (state: ApplicationState): ArkOpinion[] => getMyOpinionsRoot(state).additionalOpinions;
export const getSelectedAdditionalOpinionId = (state: ApplicationState): number | null => getMyOpinionsRoot(state).selectedAdditionalOpinionId;

export const getAdditionalOpinionUrls = (state: ApplicationState): string[] => getMyOpinionsRoot(state).additionalOpinionUrls;
export const getSelectedOpinionUrl = (state: ApplicationState): string | null => {
    const selectedAdditionalOpinionId = getSelectedAdditionalOpinionId(state);
    if (selectedAdditionalOpinionId) {
        const additionalOpinions = getAdditionalOpinions(state);
        const additionalOpinionIds = additionalOpinions.map(({ opinionId }) => opinionId);
        const selectedIndex = indexOf(selectedAdditionalOpinionId, additionalOpinionIds);
        const additionalOpinionUrls = getAdditionalOpinionUrls(state);
        return additionalOpinionUrls[selectedIndex];
    }
    return getOpinionUrl(state);
};

export const getOpinionAnalysisView = (state: ApplicationState): AnalysisView => getMyOpinionsRoot(state).opinionAnalysisView;

export const getOpinionToDelete = (state: ApplicationState): ArkOpinion | null => getMyOpinionsRoot(state).opinionToDelete;

export const getDeleteOpinionModalOpen = (state: ApplicationState): boolean => !isNull(getOpinionToDelete(state));

export const getIsDeleting = (state: ApplicationState): boolean => getMyOpinionsRoot(state).isDeleting;

export const getLinkedOpinions = (state: ApplicationState): LinkedOpinion[] => getMyOpinionsRoot(state).linkedOpinions;

export const getIncompleteOpinionSelectedPage = (state: ApplicationState): number | null => getMyOpinionsRoot(state).incompleteOpinionSelectedPage;

export const getUnauthorisedOpinion = (state: ApplicationState): LinkedOpinion | ArkOpinion | null => getMyOpinionsRoot(state).unauthorisedOpinion;
export const getUnauthorisedOpinionModalOpen = (state: ApplicationState): boolean => !isNull(getUnauthorisedOpinion(state));

export const getPotentialBaseOpinion = (state: ApplicationState): ArkOpinion | null => getMyOpinionsRoot(state).potentialBaseOpinion;
export const getPotentialBaseOpinionModalOpen = (state: ApplicationState): boolean => !isNull(getPotentialBaseOpinion(state));

export const getSelectedPageNumber = (state: ApplicationState): number | null => getMyOpinionsRoot(state).selectedPageNumber;

export const getIsDownloadingSummary = (state: ApplicationState): boolean => getMyOpinionsRoot(state).isDownloadingSummary;

export const getOpinionSummaryModalOpen = (state: ApplicationState): boolean => getMyOpinionsRoot(state).summaryPreviewModalOpen;
export const getOpinionSummaryGenerating = (state: ApplicationState): boolean => (!isNull(getOpinion(state)) ? getOpinion(state)!.isGeneratingSummary === 1 : true);

// Table Filters and Pagination
export const getOpinionsPageNumber = (state: ApplicationState): number => getMyOpinionsRoot(state).opinionPage;
export const getTotalOpinions = (state: ApplicationState): number => getMyOpinionsRoot(state).totalOpinions;
export const getOpinionFilters = (state: ApplicationState): TableFilters => getMyOpinionsRoot(state).opinionFilters;
export const getOpinionPageSize = (state: ApplicationState): number => getMyOpinionsRoot(state).opinionPageSize;
export const getOpinionColumnSort = (state: ApplicationState): ColumnSort | undefined => getMyOpinionsRoot(state).opinionColumnSort;

export const getIncompleteOpinionsPageNumber = (state: ApplicationState): number => getMyOpinionsRoot(state).incompleteOpinionPage;
export const getTotalIncompleteOpinions = (state: ApplicationState): number => getMyOpinionsRoot(state).totalIncompleteOpinions;
export const getIncompleteOpinionFilters = (state: ApplicationState): TableFilters => getMyOpinionsRoot(state).incompleteOpinionFilters;
export const getIncompleteOpinionPageSize = (state: ApplicationState): number => getMyOpinionsRoot(state).incompleteOpinionPageSize;
export const getIncompleteOpinionColumnSort = (state: ApplicationState): ColumnSort | undefined => getMyOpinionsRoot(state).incompleteOpinionColumnSort;

// Map selectors

export const getSelectedCountry = (state: ApplicationState): string | null => getMyOpinionsRoot(state).selectedCountry;

export const getAllOpinionsForMap = (state: ApplicationState): ArkMapOpinion[] => getMyOpinionsRoot(state).mapOpinions;

export const getCountryOpinions = (state: ApplicationState): CountryOpinions | null => getMyOpinionsRoot(state).countryOpinions;

export const getCountryOpinionsModalOpen = (state: ApplicationState): boolean => !isNull(getCountryOpinions(state));

export const getOpinionsMapResetZoom = (state: ApplicationState): boolean => getMyOpinionsRoot(state).resetOpinionsMapZoom;
export const getOpinionsMapZoomSteps = (state: ApplicationState): number => getMyOpinionsRoot(state).opinionsMapZoomSteps;
export const getOpinionsMapZoomUpdated = (state: ApplicationState): boolean => getMyOpinionsRoot(state).opinionsMapZoomUpdated;
export const getOpinionsMapCurrentScale = (state: ApplicationState): number => getMyOpinionsRoot(state).opinionsMapCurrentScale;
export const getOpinionsMapScaleExtent = (state: ApplicationState): [number, number] => getMyOpinionsRoot(state).opinionsMapScaleExtent;

export const getBritishOpinions = (state: ApplicationState): BritishOpinions | null => getMyOpinionsRoot(state).britishOpinions;
export const getBritishOpinionsModalOpen = (state: ApplicationState): boolean => !isNull(getBritishOpinions(state));

// Custom Filters
export const getOpinionCustomFiltersModalOpen = (state: ApplicationState): boolean => getMyOpinionsRoot(state).opinionCustomFiltersModalOpen;
export const getSelectedOpinionCustomFilterId = (state: ApplicationState): string | null => getMyOpinionsRoot(state).selectedOpinionCustomFilterId;
export const getOpinionCustomFilterHasUpdated = (state: ApplicationState): boolean => getMyOpinionsRoot(state).opinionCustomFilterHasUpdated;
export const getSelectedCustomFilterName = (state: ApplicationState): string => {
    const allOpinionFilters = getAllOpinionCustomFilters(state);
    const selectedId = getSelectedOpinionCustomFilterId(state);
    return allOpinionFilters.find(({ id }) => id === selectedId)?.filterName || '';
};
